import { AfterContentInit, Component } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';

import { ErrorHandlerService } from '@app/services/error-handler.service';
import { MfaService } from '@app/services/mfa.service';
import { NotyService } from '@app/services/noty.service';

export type ModalResult = string | null;
export enum ModalStep {
  OtpCode = 1,
  MfaSelect = 2,
  RecoveryCode = 3
}

@Component({
  selector: 'app-confirm-otp-modal',
  templateUrl: './confirm-otp-modal.component.html',
  styleUrls: ['./confirm-otp-modal.component.scss'],
  standalone: false
})
export class ConfirmOtpModalComponent implements AfterContentInit {
  form!: FormGroup;
  currentStep = ModalStep.OtpCode;
  isLoading = false;
  readonly ModalStep = ModalStep;
  readonly mfaOptions = [{ type: 'totp', description: 'auth.mfaSelectStep.options.totp' }];

  constructor(
    public modal: NgbActiveModal,
    public translateService: TranslateService,
    private formBuilder: FormBuilder,
    private errorHandlerService: ErrorHandlerService,
    private notyService: NotyService,
    private mfaService: MfaService
  ) {}

  ngAfterContentInit() {
    this.form = this.formBuilder.group({ otp: '', mfaType: this.mfaOptions[0].type, recoveryCode: '' });
  }

  next() {
    if (this.currentStep === ModalStep.OtpCode) {
      setTimeout(() => this.verifyTOTP());
    } else if (this.currentStep === ModalStep.MfaSelect) {
      this.currentStep = ModalStep.OtpCode;
    } else if (this.currentStep === ModalStep.RecoveryCode) {
      this.disableTOTP();
    }
  }

  back() {
    if (this.currentStep === ModalStep.OtpCode) {
      this.modal.dismiss();
    }

    this.currentStep = (
      {
        [ModalStep.MfaSelect]: ModalStep.OtpCode,
        [ModalStep.RecoveryCode]: ModalStep.MfaSelect
      } as Record<ModalStep, ModalStep>
    )[this.currentStep];
  }

  verifyTOTP() {
    this.isLoading = true;
    const code = this.form.get('otp');
    code.markAsTouched();
    if (code.value.length < 6) {
      code.setErrors({ server: this.translateService.instant('error_otp_required') });
      this.isLoading = false;
      return;
    }
    this.mfaService.verifyTOTP({ otp: code.value }).subscribe({
      next: () => this.modal.close(code.value as ModalResult),
      error: error => {
        this.isLoading = false;
        code.setErrors({ server: this.translateService.instant('error_' + error.message) });
      }
    });
  }

  disableTOTP() {
    this.isLoading = true;
    this.form.get('recoveryCode').markAsTouched();
    const recoveryCode = (this.form.get('recoveryCode').value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    this.mfaService
      .disableTOTP({ recoveryCode })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: () => {
          this.notyService.success(this.translateService.instant('notyService.totpDisabled'));
          this.modal.close(null as ModalResult);
        },
        error: error => {
          const handledError = this.errorHandlerService.handle(error, { ignoreNotificationOn: ['totp_recovery_code_invalid'] });
          this.form.get('recoveryCode').setErrors({ server: this.translateService.instant('error_' + handledError.message) });
        }
      });
  }
}
